import { createIcons, ShieldAlert, FileText, Wrench, CheckCircle2, Edit3, Check, X } from "lucide";
import { contextStore } from "../auth/context-store.js";
import { sessionRequestContext } from "../auth/request-context.js";
import { router } from "../router/router.js";
import { PermissionCode } from "../../permissions/permissions.js";
import { toastService } from "../design-system/components/toast.js";
import type { Recommendation } from "../../domain/recommendation/recommendation-domain.js";

class AppRecommendationsPage extends HTMLElement {
  connectedCallback() {
    this.loadData();
  }

  private escapeHtml(str: string): string {
    return (str || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private async loadData() {
    this.innerHTML = `<div style="padding: 40px; color: var(--color-text-muted);">Carregando recomendações...</div>`;
    
    try {
      const activeClientId = contextStore.get().activeClientId;
      const ctx = { 
        activeClientId, 
        userId: contextStore.get().userId,
        permissions: contextStore.get().permissions 
      } as any;
      
      const reports = await window.app.platform.reportService.listReports(ctx).catch(() => []);
      const reportMap = new Map<string, any>();
      for (const rep of reports) {
        reportMap.set(rep.id, rep);
      }

      const allRecs: any[] = [];
      const seenIds = new Set<string>();

      // 1. Fetch from official RecommendationRepository
      try {
        const directRecs: readonly Recommendation[] =
          await window.app.platform.reportService.listAllRecommendations(ctx);
        for (const rec of directRecs) {
          if (!seenIds.has(rec.id)) {
            seenIds.add(rec.id);
            const rep = reportMap.get(rec.integrityReportId);
            allRecs.push({
              id: rec.id,
              recNumber: rec.recommendationNumber || rec.code || `REC-${(allRecs.length + 1).toString().padStart(3, '0')}`,
              reportId: rec.integrityReportId,
              reportNumber: rep?.reportNumber || (rec.integrityReportId ? `${rec.integrityReportId.split('-')[0]}-${rec.integrityReportId.split('-')[1] || rec.integrityReportId}` : 'RI-EM-ELABORACAO'),
              inspectionId: rec.inspectionId,
              date: new Date(rec.createdAt || Date.now()).toLocaleDateString('pt-BR'),
              componentName: rec.componentName || 'Componente da Inspeção',
              discipline: rec.discipline,
              criticality: rec.criticality === 'HIGH' ? 'ALTA' : (rec.criticality === 'MODERATE' ? 'MÉDIA' : (rec.criticality === 'CRITICAL' ? 'CRÍTICA' : 'BAIXA')),
              description: rec.scope || '',
              zr: rec.zr || ''
            });
          }
        }
      } catch (errRepo) {
        console.warn('Could not fetch directly from recommendation repository', errRepo);
      }

      // 2. Fetch from reports
      for (const report of reports) {
         const r = report as any;
         if (r.checklistData) {
            for (const item of r.checklistData) {
               if (item.recommendations) {
                  item.recommendations.forEach((rec: any, index: number) => {
                     const recKey = rec.id || `rec-rep-${r.id}-${index}`;
                     if (!seenIds.has(recKey)) {
                       seenIds.add(recKey);
                       allRecs.push({
                          ...rec,
                          id: recKey,
                          reportId: r.id,
                          reportNumber: r.reportNumber || `${r.id.split('-')[0]}-${r.id.split('-')[1] || r.id}`,
                          inspectionId: r.inspectionId,
                          componentName: item.name,
                          recNumber: rec.code || `REC-${(index + 1).toString().padStart(3, '0')}`,
                          description: rec.description || rec.scope || '',
                          date: new Date(report.createdAt).toLocaleDateString('pt-BR')
                       });
                     }
                  });
               }
            }
         }
      }

      // 3. Scan local inspection checklist caches for newly created unsaved recommendations
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('ri_checklist_insp_')) {
          const inspId = key.replace('ri_checklist_insp_', '');
          try {
            const items = JSON.parse(localStorage.getItem(key) || '[]');
            if (Array.isArray(items)) {
              items.forEach(item => {
                if (item.recommendations && Array.isArray(item.recommendations)) {
                  item.recommendations.forEach((rec: any, index: number) => {
                    if (rec.id && !seenIds.has(rec.id)) {
                      seenIds.add(rec.id);
                      allRecs.push({
                        id: rec.id,
                        recNumber: rec.code || `REC-${(index + 1).toString().padStart(3, '0')}`,
                        reportId: '',
                        reportNumber: `INSP-${inspId}`,
                        inspectionId: inspId,
                        componentName: item.name,
                        discipline: rec.discipline || 'CALDEIRARIA',
                        criticality: rec.criticality || 'MÉDIA',
                        description: rec.description || rec.scope || '',
                        zr: rec.zr || '',
                        date: new Date().toLocaleDateString('pt-BR')
                      });
                    }
                  });
                }
              });
            }
          } catch {
            // ignore
          }
        }
      }
      
      this.renderSuccess(allRecs);
    } catch (e: any) {
      this.innerHTML = `<div style="padding: 40px; color: var(--ia-danger-fg, #e53e3e);">Erro ao carregar recomendações: ${e.message}</div>`;
    }
  }

  private renderDescritivoCell(r: any, canEdit: boolean): string {
    const rawDesc = r.description || '';
    const safeDesc = this.escapeHtml(rawDesc);
    const displayHtml = safeDesc ? safeDesc : '<span style="color: var(--color-text-muted); font-style: italic;">Sem descritivo</span>';

    return `
      <td class="col-desc" style="min-width: 280px; max-width: 440px; vertical-align: top;">
        <div class="desc-view-mode" id="desc-view-${r.id}">
          <div class="desc-display-text" style="color: var(--color-silver-light, #d9dde0); font-size: 0.85rem; line-height: 1.45; margin-bottom: 6px; word-break: break-word; white-space: pre-wrap;">${displayHtml}</div>
          ${canEdit ? `
            <button type="button" class="btn-trigger-edit-desc" data-rec-id="${r.id}" title="Editar descritivo da recomendação">
              <i data-lucide="edit-3" style="width: 12px; height: 12px;"></i> Editar
            </button>
          ` : ''}
        </div>
        ${canEdit ? `
          <div class="desc-edit-mode" id="desc-edit-${r.id}" style="display: none; flex-direction: column; gap: 8px;">
            <textarea class="desc-textarea" id="desc-input-${r.id}" rows="3" placeholder="Informe o descritivo da recomendação..." data-original="${safeDesc}">${safeDesc}</textarea>
            <div class="desc-error-msg" id="desc-err-${r.id}" style="display: none; color: var(--status-danger-solid, #ef4444); font-size: 0.75rem; font-weight: 500;"></div>
            <div style="display: flex; gap: 8px; align-items: center; justify-content: flex-start; flex-wrap: wrap;">
              <button type="button" class="btn-cancel-desc" data-rec-id="${r.id}">
                <i data-lucide="x" style="width: 12px; height: 12px;"></i> Cancelar
              </button>
              <button type="button" class="btn-save-desc" data-rec-id="${r.id}" data-report-id="${r.reportId || ''}" data-rec-number="${r.recNumber}">
                <i data-lucide="check" style="width: 12px; height: 12px;"></i> Salvar Texto
              </button>
            </div>
          </div>
        ` : ''}
      </td>
    `;
  }

  private renderSuccess(recommendations: any[]) {
    // RBAC: só por permissão. O "|| !hasRole('VIEWER')" que existia aqui
    // anulava a checagem — qualquer perfil que não fosse VIEWER passava a
    // editar, inclusive sem a permissão. Agora é a permissão real do ato.
    const canEdit = contextStore.hasPermission(PermissionCode.RecommendationUpdate)
      || contextStore.hasPermission(PermissionCode.ReportUpdate);

    // Separate by pending ZR and with ZR
    const pending = recommendations.filter(r => !r.zr || r.zr.trim() === '');
    const completed = recommendations.filter(r => r.zr && r.zr.trim() !== '');

    this.innerHTML = `
        <style>
          .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
          }
          .page-title {
            font-size: 1.5rem;
            font-weight: 500;
            color: var(--color-text-primary);
            display: flex;
            align-items: center;
            gap: 12px;
          }
          .rec-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            background: var(--color-surface);
            border-radius: 8px;
            overflow: hidden;
            border: 1px solid var(--color-border);
            margin-bottom: 32px;
          }
          .rec-table th, .rec-table td {
            padding: 12px 16px;
            text-align: left;
            border-bottom: 1px solid var(--color-border);
            font-size: 0.875rem;
          }
          .rec-table th {
            background: rgba(0,0,0,0.2);
            font-weight: 600;
            color: var(--color-text-muted);
            text-transform: uppercase;
            font-size: 0.75rem;
          }
          .rec-table tr:last-child td {
            border-bottom: none;
          }
          .zr-input {
            background: var(--color-graphite-800);
            border: 1px solid var(--color-graphite-700);
            color: var(--color-text-primary);
            padding: 6px 10px;
            border-radius: 4px;
            width: 120px;
            font-size: 0.8rem;
          }
          .zr-input:focus {
            outline: none;
            border-color: var(--color-primary);
          }
          .btn-save-zr {
            background: var(--color-primary);
            color: var(--color-text-primary);
            border: none;
            padding: 6px 12px;
            border-radius: 4px;
            cursor: pointer;
            font-size: 0.8rem;
            font-weight: 600;
            display: inline-flex;
            align-items: center;
            gap: 6px;
          }
          .btn-save-zr:hover {
            filter: brightness(1.1);
          }
          .btn-trigger-edit-desc {
            background: rgba(255, 255, 255, 0.04);
            border: 1px solid var(--ia-line, rgba(255, 255, 255, 0.15));
            color: var(--color-gold-metallic, #d4af37);
            font-size: 0.75rem;
            padding: 4px 10px;
            border-radius: 4px;
            cursor: pointer;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            font-weight: 600;
            transition: all 0.2s ease;
          }
          .btn-trigger-edit-desc:hover {
            background: rgba(212, 175, 55, 0.12);
            border-color: var(--color-gold-metallic, #d4af37);
            color: var(--color-text-primary);
          }
          .desc-textarea {
            width: 100%;
            background: var(--color-graphite-900, #0d0f12);
            border: 1px solid var(--color-border, #333);
            color: var(--color-text-primary, #fff);
            padding: 8px 10px;
            border-radius: 4px;
            font-family: inherit;
            font-size: 0.85rem;
            line-height: 1.45;
            resize: vertical;
            box-sizing: border-box;
            min-height: 70px;
          }
          .desc-textarea:focus {
            outline: none;
            border-color: var(--color-gold-metallic, #d4af37);
            box-shadow: 0 0 0 1px var(--color-gold-metallic, #d4af37);
          }
          .btn-cancel-desc {
            background: rgba(255, 255, 255, 0.06);
            border: 1px solid var(--ia-line, rgba(255, 255, 255, 0.18));
            color: var(--color-silver-light, #d9dde0);
            font-size: 0.75rem;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 500;
            display: inline-flex;
            align-items: center;
            gap: 4px;
            transition: all 0.2s ease;
          }
          .btn-cancel-desc:hover {
            background: rgba(255, 255, 255, 0.12);
            color: var(--color-text-primary);
          }
          .btn-save-desc {
            background: linear-gradient(135deg, #e6c875, #c5a059);
            border: none;
            color: var(--color-text-primary);
            font-size: 0.75rem;
            padding: 5px 14px;
            border-radius: 4px;
            cursor: pointer;
            font-weight: 700;
            display: inline-flex;
            align-items: center;
            gap: 5px;
            transition: all 0.2s ease;
            box-shadow: 0 1px 3px rgba(0,0,0,0.3);
          }
          .btn-save-desc:hover:not(:disabled) {
            filter: brightness(1.1);
            transform: translateY(-1px);
          }
          .btn-save-desc:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            background: var(--color-graphite-700, #374151);
            color: var(--color-text-muted, #9ca3af);
          }
          .crit-baixa { color: var(--status-success-text); }
          .crit-média { color: var(--status-warning-text); }
          .crit-alta { color: var(--status-danger-solid); }
        </style>
        
        <div class="page-header">
          <div class="page-title">
            <i data-lucide="shield-alert" style="color: var(--status-warning-text);"></i> Gestão de Recomendações e ZR
          </div>
        </div>

        <h3 style="color: var(--color-text-primary); margin-bottom: 12px; font-size: 1.1rem; display: flex; align-items: center; gap: 8px;">
          <i data-lucide="wrench" style="width: 18px; color: var(--status-warning-text);"></i> Pendentes de Ordem de Serviço (ZR)
        </h3>
        ${pending.length === 0 ? `
          <div style="background: rgba(16,185,129,0.1); border: 1px solid var(--status-success-solid); color: var(--status-success-text); padding: 16px; border-radius: 8px; margin-bottom: 32px; display: flex; align-items: center; gap: 8px;">
            <i data-lucide="check-circle-2"></i> Não há recomendações aguardando número de ZR. Tudo em dia!
          </div>
        ` : `
          <div style="overflow-x: auto;">
            <table class="rec-table">
              <thead>
                <tr>
                  <th>Nº Rec</th>
                  <th>Laudo Origem</th>
                  <th>Data</th>
                  <th>Componente</th>
                  <th>Disciplina</th>
                  <th>Criticidade</th>
                  <th>Descritivo</th>
                  <th style="width: 200px;">Informar ZR</th>
                </tr>
              </thead>
              <tbody>
                ${pending.map(r => `
                  <tr>
                    <td style="font-weight: 600; color: var(--color-text-muted);">${r.recNumber}</td>
                    <td><a href="#" class="report-link" data-id="${r.reportId}" style="color: var(--color-text-primary); text-decoration: underline;">${r.reportNumber}</a></td>
                    <td>${r.date}</td>
                    <td>${r.componentName}</td>
                    <td>${r.discipline}</td>
                    <td style="font-weight: 600;" class="crit-${r.criticality.toLowerCase()}">${r.criticality}</td>
                    ${this.renderDescritivoCell(r, canEdit)}
                    <td>
                      <div style="display: flex; gap: 6px;">
                        <input type="text" class="zr-input" id="zr-${r.id}" placeholder="Ex: ZR-12345">
                        <button class="btn-save-zr" data-rec-id="${r.id}" data-report-id="${r.reportId}">Salvar</button>
                      </div>
                    </td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `}

        <h3 style="color: var(--color-text-primary); margin-bottom: 12px; font-size: 1.1rem; display: flex; align-items: center; gap: 8px; margin-top: 48px;">
          <i data-lucide="check-circle-2" style="width: 18px; color: var(--status-success-text);"></i> Recomendações Encaminhadas (Com ZR)
        </h3>
        ${completed.length === 0 ? `
          <div style="color: var(--color-graphite-400); font-style: italic;">Nenhuma recomendação com ZR informada.</div>
        ` : `
          <div style="overflow-x: auto;">
            <table class="rec-table">
              <thead>
                <tr>
                  <th>Nº Rec</th>
                  <th>Laudo Origem</th>
                  <th>Data</th>
                  <th>Componente</th>
                  <th>Disciplina</th>
                  <th>Criticidade</th>
                  <th>Descritivo</th>
                  <th>Número ZR</th>
                </tr>
              </thead>
              <tbody>
                ${completed.map(r => `
                  <tr>
                    <td style="font-weight: 600; color: var(--color-text-muted);">${r.recNumber}</td>
                    <td><a href="#" class="report-link" data-id="${r.reportId}" style="color: var(--color-text-primary); text-decoration: underline;">${r.reportNumber}</a></td>
                    <td>${r.date}</td>
                    <td>${r.componentName}</td>
                    <td>${r.discipline}</td>
                    <td style="font-weight: 600;" class="crit-${r.criticality.toLowerCase()}">${r.criticality}</td>
                    ${this.renderDescritivoCell(r, canEdit)}
                    <td><span style="background: rgba(255,255,255,0.1); padding: 4px 8px; border-radius: 4px; font-family: monospace;">${r.zr}</span></td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
        `}
      `;

      createIcons({
        icons: { ShieldAlert, FileText, Wrench, CheckCircle2, Edit3, Check, X },
        nameAttr: "data-lucide",
        root: this
      });

      this.querySelectorAll('.report-link').forEach(link => {
         link.addEventListener('click', (e) => {
            e.preventDefault();
            const reportId = (e.currentTarget as HTMLElement).dataset['id'];
            router.navigate(`/reports/integrity/view/${reportId}`);
         });
      });

      // Handle Edit Descritivo Trigger
      this.querySelectorAll('.btn-trigger-edit-desc').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const target = e.currentTarget as HTMLElement;
          const recId = target.dataset['recId'];
          if (!recId) return;

          const viewEl = this.querySelector(`#desc-view-${recId}`) as HTMLElement;
          const editEl = this.querySelector(`#desc-edit-${recId}`) as HTMLElement;
          const textarea = this.querySelector(`#desc-input-${recId}`) as HTMLTextAreaElement;

          if (viewEl && editEl) {
            viewEl.style.display = 'none';
            editEl.style.display = 'flex';
            if (textarea) {
              textarea.focus();
              textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
          }
        });
      });

      // Handle Cancel Descritivo Edit
      this.querySelectorAll('.btn-cancel-desc').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const target = e.currentTarget as HTMLElement;
          const recId = target.dataset['recId'];
          if (!recId) return;

          const viewEl = this.querySelector(`#desc-view-${recId}`) as HTMLElement;
          const editEl = this.querySelector(`#desc-edit-${recId}`) as HTMLElement;
          const textarea = this.querySelector(`#desc-input-${recId}`) as HTMLTextAreaElement;
          const errDiv = this.querySelector(`#desc-err-${recId}`) as HTMLElement;

          if (textarea) {
            textarea.value = textarea.dataset['original'] || '';
          }
          if (errDiv) {
            errDiv.style.display = 'none';
            errDiv.textContent = '';
          }
          if (viewEl && editEl) {
            editEl.style.display = 'none';
            viewEl.style.display = 'block';
          }
        });
      });

      // Handle Save Descritivo
      this.querySelectorAll('.btn-save-desc').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.preventDefault();
          const target = e.currentTarget as HTMLButtonElement;
          const recId = target.dataset['recId'];
          const reportId = target.dataset['reportId'];
          const recNumber = target.dataset['recNumber'];
          if (!recId) return;

          const textarea = this.querySelector(`#desc-input-${recId}`) as HTMLTextAreaElement;
          const errDiv = this.querySelector(`#desc-err-${recId}`) as HTMLElement;
          const cancelBtn = this.querySelector(`.btn-cancel-desc[data-rec-id="${recId}"]`) as HTMLButtonElement;

          const newText = textarea ? textarea.value.trim() : '';

          // Validation: Non-empty description
          if (!newText) {
            if (errDiv) {
              errDiv.textContent = 'Informe o descritivo da recomendação.';
              errDiv.style.display = 'block';
            }
            if (textarea) textarea.focus();
            return;
          }

          if (errDiv) {
            errDiv.style.display = 'none';
            errDiv.textContent = '';
          }

          // Feedback visual: SALVANDO...
          const originalBtnHtml = target.innerHTML;
          target.disabled = true;
          if (cancelBtn) cancelBtn.disabled = true;
          target.innerHTML = `SALVANDO...`;

          try {
            const ctx = { 
              activeClientId: contextStore.get().activeClientId,
              userId: contextStore.get().userId,
              permissions: contextStore.get().permissions
            } as any;

            // 1. UPDATE official Recommendation entity in database/repository by real ID
            try {
              if (window.app.platform.reportService.updateRecommendationDescription) {
                await window.app.platform.reportService.updateRecommendationDescription(ctx, recId as any, newText);
              }
            } catch (errRec: any) {
              console.warn('Could not update in recommendationRepo directly', errRec);
            }

            // 2. Sync with report if exists AND report is in elaboration (not frozen/completed)
            if (reportId) {
              try {
                const report = await window.app.platform.reportService.getReportById(ctx, reportId as any);
                if (report) {
                  const isFrozen = report.status === 'COMPLETED' || report.status === 'SIGNED' || report.status === 'APPROVED';
                  if (!isFrozen) {
                    const r = report as any;
                    let reportUpdated = false;
                    if (r.checklistData && Array.isArray(r.checklistData)) {
                      for (const item of r.checklistData) {
                        if (item.recommendations) {
                          const rec = item.recommendations.find((rc: any) => rc.id === recId || rc.code === recId || rc.code === recNumber);
                          if (rec) {
                            rec.description = newText;
                            rec.scope = newText;
                            reportUpdated = true;
                          }
                        }
                      }
                    }
                    if (r.inspectionObservations) {
                      try {
                        const obs = typeof r.inspectionObservations === 'string' ? JSON.parse(r.inspectionObservations) : r.inspectionObservations;
                        if (Array.isArray(obs)) {
                          for (const item of obs) {
                            if (item.recommendations && Array.isArray(item.recommendations)) {
                              const rec = item.recommendations.find((rc: any) => rc.id === recId || rc.code === recId || rc.code === recNumber);
                              if (rec) {
                                rec.description = newText;
                                rec.scope = newText;
                                reportUpdated = true;
                              }
                            }
                          }
                          if (reportUpdated) {
                            r.inspectionObservations = typeof r.inspectionObservations === 'string' ? JSON.stringify(obs) : obs;
                          }
                        }
                      } catch {
                        // not json
                      }
                    }
                    if (reportUpdated) {
                      await window.app.platform.reportService.updateReport(ctx, report);
                    }
                  }
                }
              } catch (errRep) {
                console.warn('Could not sync description to report entity', errRep);
              }
            }

            // 3. Clear report document snapshots cache to force fresh regeneration in Viewer and PDF
            try {
              if (reportId) {
                localStorage.removeItem(`ri_cache_${reportId}`);
                localStorage.removeItem(`ri_checklist_${reportId}`);
              }
              for (let i = localStorage.length - 1; i >= 0; i--) {
                const k = localStorage.key(i);
                // --- PROTECTED CONFIGURATION ---
                // DO NOT REMOVE !k.startsWith('ri_checklist_insp_')
                // We must NEVER delete the active inspection draft cache, otherwise
                // pending edits will be lost and desynced from the Checklist and Report.
                // -------------------------------
                if (k && (k.startsWith('ri_cache_') || (k.startsWith('ri_checklist_') && !k.startsWith('ri_checklist_insp_')))) {
                  localStorage.removeItem(k);
                }
              }
            } catch {}

            // 4. Sync with local inspection checklist caches (ri_checklist_insp_*)
            for (let i = 0; i < localStorage.length; i++) {
              const key = localStorage.key(i);
              if (key && (key.startsWith('ri_checklist_insp_') || key.startsWith('ri_checklist_'))) {
                try {
                  const items = JSON.parse(localStorage.getItem(key) || '[]');
                  let changed = false;
                  if (Array.isArray(items)) {
                    items.forEach((item: any) => {
                      if (item.recommendations && Array.isArray(item.recommendations)) {
                        item.recommendations.forEach((rec: any) => {
                          if (rec.id === recId || rec.code === recId || rec.code === recNumber) {
                            rec.description = newText;
                            rec.scope = newText;
                            changed = true;
                          }
                        });
                      }
                    });
                  }
                  if (changed) {
                    localStorage.setItem(key, JSON.stringify(items));
                  }
                } catch {
                  // ignore
                }
              }
            }

            // 5. Notify any listening components
            window.dispatchEvent(new CustomEvent('ri_checklist_updated', { detail: { reportId } }));
            window.dispatchEvent(new CustomEvent('ri_recommendations_updated', { detail: { recommendationId: recId, reportId } }));

            // 6. Visual feedback
            toastService.success('Texto da recomendação atualizado com sucesso.');

            // 7. Reload UI
            await this.loadData();
          } catch (err: any) {
            console.error('Error updating recommendation description:', err);
            target.disabled = false;
            if (cancelBtn) cancelBtn.disabled = false;
            target.innerHTML = originalBtnHtml;
            toastService.error('Não foi possível atualizar o texto da recomendação.');
          }
        });
      });

      this.querySelectorAll('.btn-save-zr').forEach(btn => {
         btn.addEventListener('click', async (e) => {
            const target = e.currentTarget as HTMLElement;
            const recId = target.dataset['recId'];
            const reportId = target.dataset['reportId'];
            const input = this.querySelector(`#zr-${recId}`) as HTMLInputElement;
            
            if (input && input.value.trim() !== '') {
               target.innerHTML = '...';
               try {
                  const zrValue = input.value.trim();
                  const ctx = sessionRequestContext();

                  // 1. Update official Recommendation entity in database/repository
                  try {
                    if (window.app.platform.reportService.updateRecommendationZR) {
                      await window.app.platform.reportService.updateRecommendationZR(ctx, recId as any, zrValue);
                    }
                  } catch (errRec) {
                    console.warn('Could not update in recommendationRepo directly', errRec);
                  }

                  // 2. Sync with report if exists
                  if (reportId) {
                    try {
                      const report = await window.app.platform.reportService.getReportById(ctx, reportId as any);
                      if (report) {
                        const r = report as any;
                        let reportUpdated = false;
                        if (r.checklistData && Array.isArray(r.checklistData)) {
                          for (const item of r.checklistData) {
                            if (item.recommendations) {
                              const rec = item.recommendations.find((r: any) => r.id === recId || r.code === recId);
                              if (rec) {
                                rec.zr = zrValue;
                                reportUpdated = true;
                                break;
                              }
                            }
                          }
                        }
                        if (r.inspectionObservations) {
                          try {
                            const obs = typeof r.inspectionObservations === 'string' ? JSON.parse(r.inspectionObservations) : r.inspectionObservations;
                            if (Array.isArray(obs)) {
                              for (const item of obs) {
                                if (item.recommendations && Array.isArray(item.recommendations)) {
                                  const rec = item.recommendations.find((r: any) => r.id === recId || r.code === recId);
                                  if (rec) {
                                    rec.zr = zrValue;
                                    reportUpdated = true;
                                  }
                                }
                              }
                              if (reportUpdated) {
                                r.inspectionObservations = typeof r.inspectionObservations === 'string' ? JSON.stringify(obs) : obs;
                              }
                            }
                          } catch {
                            // not json
                          }
                        }
                        if (reportUpdated) {
                          await window.app.platform.reportService.updateReport(ctx, report);
                        }
                      }
                    } catch (errRep) {
                      console.warn('Could not sync ZR to report entity', errRep);
                    }
                  }

                  // Invalidate report document cache to force regeneration with new ZR
                  try {
                    if (reportId) {
                      localStorage.removeItem(`ri_cache_${reportId}`);
                      localStorage.removeItem(`ri_checklist_${reportId}`);
                    }
                    for (let i = localStorage.length - 1; i >= 0; i--) {
                      const k = localStorage.key(i);
                      // --- PROTECTED CONFIGURATION ---
                      // DO NOT REMOVE !k.startsWith('ri_checklist_insp_')
                      // We must NEVER delete the active inspection draft cache, otherwise
                      // pending edits will be lost and desynced from the Checklist and Report.
                      // -------------------------------
                      if (k && (k.startsWith('ri_cache_') || (k.startsWith('ri_checklist_') && !k.startsWith('ri_checklist_insp_')))) {
                        localStorage.removeItem(k);
                      }
                    }
                  } catch {}

                  // 3. Sync with any active local inspection checklists
                  for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i);
                    if (key && (key.startsWith('ri_checklist_insp_') || key.startsWith('ri_checklist_'))) {
                      try {
                        const items = JSON.parse(localStorage.getItem(key) || '[]');
                        let changed = false;
                        if (Array.isArray(items)) {
                          items.forEach(item => {
                            if (item.recommendations && Array.isArray(item.recommendations)) {
                              item.recommendations.forEach((rec: any) => {
                                if (rec.id === recId || rec.code === recId) {
                                  rec.zr = zrValue;
                                  changed = true;
                                }
                              });
                            }
                          });
                        }
                        if (changed) {
                          localStorage.setItem(key, JSON.stringify(items));
                        }
                      } catch {
                        // ignore
                      }
                    }
                  }

                  this.loadData(); // Reload UI
               } catch (err) {
                  alert('Erro ao salvar ZR');
                  target.innerHTML = 'Salvar';
               }
            }
         });
      });
  }
}

if (!customElements.get("app-recommendations-page")) {
  customElements.define("app-recommendations-page", AppRecommendationsPage);
}
